// Archive helpers for deliverables that were never finished and are no longer
// relevant. Kept import-free so Deno CI can unit-test them in isolation.

export type ArchiveReason =
  | 'no-longer-relevant'
  | 'cancelled'
  | 'duplicate'
  | 'superseded'
  | 'other';

export const ARCHIVE_REASONS: ReadonlyArray<{ value: ArchiveReason; label: string; hint: string }> = [
  { value: 'no-longer-relevant', label: 'No longer relevant', hint: 'The need went away or priorities moved on.' },
  { value: 'cancelled',          label: 'Cancelled',          hint: 'The show, event, or request was called off.' },
  { value: 'duplicate',          label: 'Duplicate',          hint: 'The same work is tracked in another deliverable.' },
  { value: 'superseded',         label: 'Replaced',           hint: 'A different deliverable now covers this.' },
  { value: 'other',              label: 'Other',              hint: 'Explain in the note.' },
];

export interface DeliverableArchiveFields {
  status?: string;
  archived?: boolean;
  archivedAt?: string | null;
  archivedBy?: string | null;
  archiveReason?: ArchiveReason | null;
  archiveNote?: string | null;
}

/** A finished deliverable stays part of the project record even once archived. */
function isFinished(d: Pick<DeliverableArchiveFields, 'status'>): boolean {
  return d.status === 'complete' || d.status === 'approved';
}

/**
 * Archived *and* never finished: the work was dropped. These no longer count
 * toward project progress, workload, digests, or volunteer recruiting. A
 * finished deliverable that was archived just to declutter keeps counting as
 * completed.
 */
export function isAbandoned(d: DeliverableArchiveFields): boolean {
  return !!d.archived && !isFinished(d);
}

/**
 * Progress over a project's deliverables. Abandoned work counts toward neither
 * side, so dropping a deliverable stops it dragging the percentage down;
 * finished work that was archived still counts as done.
 */
export function summarizeCompletion(
  deliverables: DeliverableArchiveFields[],
): { completed: number; total: number; percentage: number } {
  const inScope = deliverables.filter(d => !isAbandoned(d));
  const completed = inScope.filter(isFinished).length;
  const total = inScope.length;
  return { completed, total, percentage: total > 0 ? Math.round((completed / total) * 100) : 0 };
}

export function getArchiveReasonLabel(reason: string | null | undefined): string | undefined {
  return ARCHIVE_REASONS.find(r => r.value === reason)?.label;
}

export interface ArchivePatchInput {
  status?: string;
  reason?: ArchiveReason | null;
  note?: string | null;
  actor?: string | null;
  now?: Date;
}

/**
 * Fields to PUT when archiving. Reason only applies to unfinished work; a
 * finished deliverable is archived without one.
 */
export function buildArchivePatch(input: ArchivePatchInput): Required<
  Pick<DeliverableArchiveFields, 'archived' | 'archivedAt' | 'archivedBy' | 'archiveReason' | 'archiveNote'>
> {
  const note = typeof input.note === 'string' ? input.note.trim() : '';
  const actor = typeof input.actor === 'string' ? input.actor.trim() : '';
  const unfinished = !isFinished({ status: input.status });
  return {
    archived: true,
    archivedAt: (input.now ?? new Date()).toISOString(),
    archivedBy: actor || null,
    archiveReason: unfinished ? (input.reason ?? 'no-longer-relevant') : null,
    archiveNote: note || null,
  };
}

/**
 * Fields to PUT when restoring. Explicit nulls rather than omitted keys:
 * JSON.stringify drops undefined and the server merges onto the stored
 * record, so an omitted key would leave the old value behind.
 */
export function buildRestorePatch(): Required<
  Pick<DeliverableArchiveFields, 'archived' | 'archivedAt' | 'archivedBy' | 'archiveReason' | 'archiveNote'>
> {
  return {
    archived: false,
    archivedAt: null,
    archivedBy: null,
    archiveReason: null,
    archiveNote: null,
  };
}
